/**
 * MJML's head `img { height: auto }` beats a height attribute and squared the signature's
 * spacer shims (monday 13065351364); pin a shim's height (or a real image's ratio) inline instead.
 */
export function pinSignatureImageSizes(html: string): string {
	return html.replace(/<img\b[^>]*>/gi, tag => {
		// (?<=\s): a real attribute, never `data-height` or `?height=` inside a src.
		const attr = (name: string) =>
			tag.match(new RegExp(`(?<=\\s)${name}\\s*=\\s*["']?(\\d+)(?:px)?["']?(?=[\\s/>])`, 'i'))?.[1];
		const height = attr('height');
		if (!height) return tag;
		const width = attr('width');

		const styleMatch = /(?<=\s)style\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/i.exec(tag);
		const raw = styleMatch?.[1] ?? '';
		const quote = raw.startsWith("'") ? "'" : '"';
		const style = raw.replace(/^["']|["']$/g, '');
		if (/(^|;)\s*(height|aspect-ratio)\s*:/i.test(style)) return tag;

		// Gmail drops an inline aspect-ratio but keeps height, so a thin spacer shim (20px or
		// less on a side - its 1x1 gif would square up) gets a fixed height. Real images keep a
		// ratio: their own shape already matches, and they still scale when max-width clamps them.
		const w = Number(width);
		const h = Number(height);
		const isShim = h <= 20 || (width !== undefined && w <= 20);
		const pin = isShim || !(w > 0 && h > 0) ? `height:${height}px` : `aspect-ratio:${width}/${height}`;
		const merged = style.trim() ? `${style.trim().replace(/;?$/, ';')}${pin}` : pin;
		if (!styleMatch) return tag.replace(/^<img\b/i, () => `<img style="${merged}"`);

		const start = styleMatch.index;
		const end = start + styleMatch[0].length;
		return `${tag.slice(0, start)}style=${quote}${merged}${quote}${tag.slice(end)}`;
	});
}

// The frame Office's signature templates put on the outermost table, in any token order.
const isTemplateFrame = (decl: string): boolean => {
	const match = /^\s*border\s*:\s*(.*?)\s*$/i.exec(decl);
	if (!match) return false;
	const parts = match[1].toLowerCase().split(/\s+/).map(v => (v === '#ccc' ? '#cccccc' : v));
	return parts.length === 3 && ['#cccccc', '1px', 'solid'].every(v => parts.includes(v));
};

/**
 * Let a fixed-width signature table (e.g. width="575") shrink to fit a narrower block
 * instead of overflowing it, and drop the gray frame Office's signature templates put
 * on the outermost table (style="border:#cccccc 1px solid") -- the block is the frame
 * now (monday 13065351364). Only that frame goes: an agent's own border, and inner
 * tables' borders, are kept.
 */

export function fitSignatureTables(html: string): string {
	let isOuter = true;
	return html.replace(/<table\b[^>]*>/gi, tag => {
		const stripBorder = isOuter;
		isOuter = false;
		const hasPixelWidth = /(?<=\s)width\s*=\s*["']?\d+(?:px)?["']?(?=[\s/>])/i.test(tag);
		const styleMatch = /(?<=\s)style\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/i.exec(tag);
		const raw = styleMatch?.[1] ?? '';
		const quote = raw.startsWith("'") ? "'" : '"';
		let style = raw.replace(/^["']|["']$/g, '').trim();
		const original = style;

		if (stripBorder) {
			style = style
				.split(';')
				.filter(decl => !isTemplateFrame(decl))
				.join(';')
				.replace(/^;+|;+$/g, '')
				.trim();
		}
		if (hasPixelWidth && !/max-width\s*:/i.test(style)) {
			style = style ? `${style.replace(/;?$/, ';')}max-width:100%` : 'max-width:100%';
		}
		if (style === original) return tag;

		if (!styleMatch) return tag.replace(/^<table\b/i, () => `<table style="${style}"`);
		const start = styleMatch.index;
		return `${tag.slice(0, start)}style=${quote}${style}${quote}${tag.slice(start + styleMatch[0].length)}`;
	});
}
